import React, { useState, useEffect } from 'react';
import { DashboardService, Spinner, Alert } from '../../services';
import { Link, useNavigate } from 'react-router-dom';
import Pagination from '../../components/Pagination';
import 'react-loading-skeleton/dist/skeleton.css';
import { ThreeDotIcon } from '../../components/icons/IconProvider';
import { formatValue } from '../../utils/Helper';
import NoDataFound from '../../components/NoDataFound';
import { formatDate } from '../../services/config.service';
import { ThreeDotLoaderIcon } from '../../components/icons/IconProvider';
import exactMathNode from 'exact-math';
import TableComponent from '../../components/TableComponent';
import StatusBadge from '../../components/StatusBadge';
import SortByFilter from '../../components/listing/SortByFilter';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({});
  const [projectList, setProjecList] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [isDashBoardLoader, setDashBoardLoader] = useState(false);
  const [isLoader, setLoader] = useState(false);
  const [selectedOption, setSelectedOption] = useState('new_to_old');
  const [isPagination, setPagination] = useState({
    totalPages: 10,
    currentPage: 1,
  });

  const [filterData, setFilterData] = useState({
    perPage: perPage,
    page: page,
    commonSearch: '',
    status: '',
    sortBy: 'new_to_old',
    investmentType: '',
  });

  useEffect(() => {
    getDashboardData();
    getProjectList();
  }, [filterData]);

  const getDashboardData = async () => {
    setDashBoardLoader(true);
    try {
      let response = await DashboardService.getDashboardData();
      if (response.status === 'failure') {
        setDashBoardLoader(false);
        Alert.error(response.message);
        return false;
      }

      setDashboardData(response.data);
      setDashBoardLoader(false);
    } catch (error) {
      setDashBoardLoader(false);
      Alert.error(error.message);
    }
  };

  const getProjectList = async () => {
    try {
      setLoader(true);
      let response = await DashboardService.getProjectList(
        filterData.perPage,
        filterData.page,
        filterData.commonSearch,
        filterData.status,
        filterData.sortBy,
        filterData.investmentType,
      );

      if (response.status === 'failure') {
        setLoader(false);
        Alert.error(response.message);
        return false;
      }
      setPagination({
        totalPages: response.data.pagination.totalPages,
        currentPage: response.data.pagination.page,
      });
      setProjecList([...response.data.records]);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      Alert.error(error.message);
    }
  };

  const searchData = async (action, value) => {
    try {
      if (action === 'investmentType' && value === filterData.investmentType)
        return false;

      setLoader(true);
      setFilterData({
        commonSearch: '',
      });
      setFilterData({
        ...filterData,
        ...(action === 'commonSearch' && { page: 1 }),
        ...(action === 'investmentType' && { commonSearch: '' }),
        [action]: value,
      });
    } catch (error) {
      Spinner.hide();
      Alert.error(error.message);
    }
  };

  const handlePagination = (current) => {
    setPagination({ ...isPagination, currentPage: current });
    setFilterData({
      ...filterData,
      page: current,
    });
  };

  return (
    <>
      <div className="">
        <h3 className="mb-2">Dashboard</h3>
        <DashboardStatistics
          dashboardData={dashboardData}
          isDashBoardLoader={isDashBoardLoader}
        />

        <NextPayoutDue
          dashboardData={dashboardData}
          isDashBoardLoader={isDashBoardLoader}
        />

        <ProjectsList
          projectList={projectList}
          isLoader={isLoader}
          searchData={searchData}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          isPagination={isPagination}
          handlePagination={handlePagination}
          filterData={filterData}
          setFilterData={setFilterData}
          setPage={setPage}
        />
      </div>
    </>
  );
};
export default Dashboard;

const investmentTypeSort = [
  { name: 'All Investment type', value: '' },
  { name: 'Equity', value: 'asset' },
  { name: 'Bond', value: 'bond' },
];

const sortOptions = [
  { name: 'Select Filter', value: '' },
  { name: 'New to Old', value: 'new_to_old' },
  { name: 'Old to New', value: 'old_to_new' },
  { name: 'Low to High Cost', value: 'low_to_high' },
  { name: 'High to Low Cost', value: 'high_to_low' },
];

const ProjectsList = ({
  projectList,
  isLoader,
  isPagination,
  handlePagination,
  filterData,
  setFilterData,
  setPage,
}) => {
  const navigate = useNavigate();

  const tableConfig = {
    columns: {
      project_name: {
        title: ' Project Name',
        render: (data) => {
          return data?.name ? data.name : 'NA';
        },
      },
      type: {
        title: 'Type',
        render: (data) => {
          return (
            <>
              <StatusBadge
                status={data.investment_type === 'bond' ? 'Bond' : 'Equity'}
              />
            </>
          );
        },
      },
      tokens_sold: {
        title: 'Sold Tokens',
        render: (data) => {
          return (
            <span className=" inline-block font-semibold">
              {String(data?.sold_quantity)}
            </span>
          );
        },
      },
      nominal_value: {
        title: 'Nominal Value',
        render: (data) => {
          return (
            <span className=" inline-block font-semibold">
              {data?.nominal_value
                ? formatValue(data?.nominal_value, {
                    decimalPlaces: 2,
                  })
                : '0.00'}
            </span>
          );
        },
      },
      value_sold: {
        title: 'Sold Value',
        render: (data) => {
          return (
            <span className=" inline-block font-semibold">
              {data?.total_invested_amount
                ? formatValue(data?.total_invested_amount, {
                    decimalPlaces: 2,
                  })
                : '0.00'}
            </span>
          );
        },
      },
      available_supply_sticker: {
        title: 'Available Supply',
        render: (data) => {
          // Calculate the available quantity
          const availableQuantity = exactMathNode.sub(
            data?.available_for_sale,
            Number(data?.sold_quantity),
          );
          return (
            <>
              <span className="inline-block font-semibold">
                {availableQuantity || '0'} of {data?.available_for_sale || '0'}
                {data?.lock_qty_total && data?.lock_qty_total > 0
                  ? ` (${data?.lock_qty_total} locked)`
                  : ''}
              </span>
            </>
          );
        },
      },
      status: {
        title: 'Status',
        render: (data) => {
          const availableQuantity = exactMathNode.sub(
            data?.available_for_sale,
            Number(data?.sold_quantity),
          );
          return (
            <>
              {availableQuantity === 0 ? (
                <span className="inline-block uppercase bg-[#f44336] text-white px-2 rounded-md pt-1 pb-[2px] font-semibold text-xs">
                  Sold out
                </span>
              ) : (
                <>
                  <StatusBadge
                    status={
                      data?.project_status === 0
                        ? 'project_draft'
                        : 'project_published'
                    }
                  />
                </>
              )}
            </>
          );
        },
      },
      est_payout_due: {
        title: 'Est Payout',
        render: (data) => {
          return (
            <span className=" inline-block font-semibold">
              {data?.next_payout_amount
                ? formatValue(data?.next_payout_amount, { decimalPlaces: 2 })
                : 'n.a.'}
            </span>
          );
        },
      },
    },
    data: projectList,
  };

  const projectRedirect = (project_id) => {
    navigate('/assets/view/' + project_id);
  };

  return (
    <>
      <div className="mt-3">
        <h4 className="mb-2">Projects</h4>
      </div>

      <div className=" bg-[#f3f3f3] mt-4 px-5 pt-4 border-b-2 rounded-t-lg pb-4">
        <div className="md:text-right">
          <div className="inline-flex items-end  rounded-md ml-4 mr-2">
            <SortByFilter
              value={filterData.investmentType}
              dropdownList={investmentTypeSort}
              filterData={filterData}
              setFilterData={setFilterData}
              setPage={setPage}
              fieldName="investmentType"
            />
          </div>

          <SortByFilter
            value={filterData.sortBy}
            dropdownList={sortOptions}
            filterData={filterData}
            setFilterData={setFilterData}
            setPage={setPage}
            fieldName="sortBy"
          />
        </div>
      </div>

      <div className="bg-[#F3F3F3] px-5">
        <div className="flex  flex-col relative overflow-x-auto">
          <TableComponent
            tableConfig={tableConfig}
            isListLoader={isLoader}
            isList={projectList}
            redirectTo={projectRedirect}
          />
          {projectList && projectList.length === 0 && isLoader === false && (
            <>
              <NoDataFound />
            </>
          )}
        </div>
        {projectList && projectList.length > 0 && isLoader === false && (
          <Pagination
            total={isPagination.totalPages}
            current={isPagination.currentPage}
            pagination={(crPage) => handlePagination(crPage)}
          />
        )}
      </div>
    </>
  );
};

const DashboardStatistics = ({ dashboardData, isDashBoardLoader }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Link to={'/assets'}>
        <div className="bg-[#A7D760] px-screen p-4 xl:p-8 rounded-lg">
          <p className=" dashboard-title-text">Total Projects</p>
          <p className="dashboard-title-value">
            {isDashBoardLoader ? (
              <ThreeDotIcon />
            ) : dashboardData?.totalProjects ? (
              dashboardData?.totalProjects
            ) : (
              '0'
            )}
          </p>
        </div>
      </Link>
      <Link to={'/users/list'}>
        <div className="bg-[#E79853] px-screen p-4 xl:p-8 rounded-lg ">
          <p className=" dashboard-title-text">Number of Investors</p>
          <p className="dashboard-title-value">
            {isDashBoardLoader ? (
              <ThreeDotIcon />
            ) : (
              <>
                {dashboardData?.totalInvestors}
                <span className="text-sm">{` (${dashboardData?.totalUsers} accounts)`}</span>
              </>
            )}
          </p>
        </div>
      </Link>
      <Link to={'/assets'}>
        <div className="bg-[#9F9787] px-screen p-4 xl:p-8 rounded-lg ">
          <p className=" dashboard-title-text">Total Invested</p>
          <p className="dashboard-title-value">
            {isDashBoardLoader ? (
              <ThreeDotIcon />
            ) : dashboardData?.totalInvestment ? (
              formatValue(dashboardData?.totalInvestment, {
                decimalPlaces: 2,
              })
            ) : (
              '0'
            )}
          </p>
        </div>
      </Link>
      <Link to={'/payout/list'}>
        <div className="bg-[#3D4CB2] px-screen p-4 xl:p-8 rounded-lg ">
          <p className=" dashboard-title-text ">Est Next Payout Total</p>
          <p className="dashboard-title-value">
            {isDashBoardLoader ? (
              <ThreeDotIcon />
            ) : dashboardData?.estimatedNextPayoutAmount ? (
              formatValue(dashboardData?.estimatedNextPayoutAmount, {
                decimalPlaces: 2,
              })
            ) : (
              '0'
            )}
          </p>
        </div>
      </Link>
    </div>
  );
};

const NextPayoutDue = ({ dashboardData, isDashBoardLoader }) => {
  return (
    <>
      <div className="mt-3">
        <h4 className="mb-2">Next Payments Due</h4>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#f3f3f3] px-screen p-4 xl:p-4 rounded-lg col-span-2 md:col-span-4">
          <div className="grid md:grid-cols-5 grid-cols-1 gap-4">
            {isDashBoardLoader ? (
              <ThreeDotLoaderIcon />
            ) : dashboardData?.nextPayouts?.length > 0 ? (
              dashboardData?.nextPayouts.map((payout, index) => (
                <div className="px-4 py-3 bg-white rounded-md" key={index}>
                  <p className="text-2xl font-semibold mb-3">
                    {payout?.payoutAmount
                      ? formatValue(payout?.payoutAmount, {
                          decimalPlaces: 2,
                        })
                      : '0.00'}
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm">
                      Due Date:{' '}
                      <b>{formatDate(payout?.payoutDate, 'DD MMM YYYY')}</b>
                      <Link
                        to={`/assets/view/` + payout?.projectId}
                        className="text-sm text-blue-600 font-medium border-b ml-2 border-blue-600 pb-0.5"
                      >
                        {payout?.symbol}
                      </Link>
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p>There are no upcoming payouts due at this time.</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
